import { z } from 'zod';
import { EngineTunable } from './engine';
import { CurrencyCode, isSupportedCurrency, MinorUnits, SignedMinorUnits } from './money';
import { GameKind, GameType } from './operator';
import { GameRoundResult } from './result';

/**
 * Admin API contract. Protected by a static API key rather than HMAC signing.
 * Used to manage the game catalogue, operators (tenants) and per-tenant
 * enablement. In production this is called by deployment tooling, not a UI.
 */

export const ADMIN_API_KEY_HEADER = 'x-admin-api-key';

// ---------------------------------------------------------------------------
// Games
// ---------------------------------------------------------------------------

export const CreateGameRequest = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(128),
  type: GameType.default('DICE'),
  kind: GameKind.default('INSTANT'),
  mathVersion: z.string().min(1).max(64),
  rtp: z.number().min(0).max(1),
  volatility: z.enum(['LOW', 'MEDIUM', 'HIGH']).default('MEDIUM'),
  reels: z.number().int().min(0).default(0),
  rows: z.number().int().min(0).default(0),
  lines: z.number().int().min(0).default(0),
  features: z.array(z.string()).default([]),
  maxWinMultiplier: z.number().min(0).default(0),
  /** Global default house edge in basis points. NULL = use engine default. */
  defaultHouseEdgeBps: z.number().int().min(0).max(9999).nullable().optional(),
  thumbnailUrl: z.string().optional(),
  supportedLanguages: z.array(z.string()).default(['en']),
  supportedPlatforms: z.array(z.enum(['DESKTOP', 'MOBILE'])).default(['DESKTOP', 'MOBILE']),
  demoAvailable: z.boolean().default(true),
  tags: z.array(z.string()).default([]),
  isNew: z.boolean().default(false),
  isHot: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
});
export type CreateGameRequest = z.infer<typeof CreateGameRequest>;

export const UpdateGameRequest = z.object({
  name: z.string().min(1).max(128).optional(),
  mathVersion: z.string().min(1).max(64).optional(),
  rtp: z.number().min(0).max(1).optional(),
  volatility: z.enum(['LOW', 'MEDIUM', 'HIGH']).optional(),
  status: z.enum(['ACTIVE', 'DISABLED']).optional(),
  reels: z.number().int().min(0).optional(),
  rows: z.number().int().min(0).optional(),
  lines: z.number().int().min(0).optional(),
  features: z.array(z.string()).optional(),
  maxWinMultiplier: z.number().min(0).optional(),
  /** Global default house edge in basis points. NULL clears the override. */
  defaultHouseEdgeBps: z.number().int().min(0).max(9999).nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  supportedLanguages: z.array(z.string()).optional(),
  supportedPlatforms: z.array(z.enum(['DESKTOP', 'MOBILE'])).optional(),
  demoAvailable: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  isNew: z.boolean().optional(),
  isHot: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type UpdateGameRequest = z.infer<typeof UpdateGameRequest>;

export const AdminGameView = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  kind: GameKind,
  mathVersion: z.string(),
  rtp: z.number(),
  volatility: z.string(),
  reels: z.number(),
  rows: z.number(),
  lines: z.number(),
  features: z.array(z.string()),
  maxWinMultiplier: z.number(),
  defaultHouseEdgeBps: z.number().nullable(),
  thumbnailUrl: z.string().nullable(),
  supportedLanguages: z.array(z.string()),
  supportedPlatforms: z.array(z.string()),
  demoAvailable: z.boolean(),
  tags: z.array(z.string()),
  isNew: z.boolean(),
  isHot: z.boolean(),
  sortOrder: z.number(),
  status: z.enum(['ACTIVE', 'DISABLED']),
  createdAt: z.string(),
});
export type AdminGameView = z.infer<typeof AdminGameView>;

export const AdminGameListResponse = z.object({
  status: z.literal('OK'),
  games: z.array(AdminGameView),
});
export type AdminGameListResponse = z.infer<typeof AdminGameListResponse>;

export const AdminGameResponse = z.object({
  status: z.literal('OK'),
  game: AdminGameView,
});
export type AdminGameResponse = z.infer<typeof AdminGameResponse>;

// ---------------------------------------------------------------------------
// Operators (tenants)
// ---------------------------------------------------------------------------

export const CreateOperatorRequest = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(128),
  callbackBaseUrl: z.string().url(),
  allowedIps: z.array(z.string()).default([]),
  defaultCurrency: CurrencyCode.default('USD'),
});
export type CreateOperatorRequest = z.infer<typeof CreateOperatorRequest>;

export const UpdateOperatorRequest = z.object({
  name: z.string().min(1).max(128).optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
  callbackBaseUrl: z.string().url().optional(),
  allowedIps: z.array(z.string()).optional(),
  defaultCurrency: CurrencyCode.optional(),
});
export type UpdateOperatorRequest = z.infer<typeof UpdateOperatorRequest>;

export const AdminOperatorView = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['ACTIVE', 'SUSPENDED']),
  callbackBaseUrl: z.string(),
  allowedIps: z.array(z.string()),
  defaultCurrency: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminOperatorView = z.infer<typeof AdminOperatorView>;

export const AdminOperatorListResponse = z.object({
  status: z.literal('OK'),
  operators: z.array(AdminOperatorView),
});
export type AdminOperatorListResponse = z.infer<typeof AdminOperatorListResponse>;

export const AdminOperatorResponse = z.object({
  status: z.literal('OK'),
  operator: AdminOperatorView,
});
export type AdminOperatorResponse = z.infer<typeof AdminOperatorResponse>;

// ---------------------------------------------------------------------------
// Operator keys
// ---------------------------------------------------------------------------

export const CreateOperatorKeyRequest = z.object({
  keyId: z.string().min(1).max(64),
});
export type CreateOperatorKeyRequest = z.infer<typeof CreateOperatorKeyRequest>;

export const AdminOperatorKeyResponse = z.object({
  status: z.literal('OK'),
  keyId: z.string(),
  /** Returned exactly once on creation. */
  secret: z.string(),
});
export type AdminOperatorKeyResponse = z.infer<typeof AdminOperatorKeyResponse>;

// ---------------------------------------------------------------------------
// Per-tenant game enablement
// ---------------------------------------------------------------------------

export const SetOperatorGameRequest = z.object({
  enabled: z.boolean(),
  /** Per-operator house edge override for adjustable games. Null clears. */
  houseEdgeBps: z.number().int().min(0).max(9999).nullable().optional(),
  /**
   * Everything else this operator configured for this game, keyed by the
   * tunables the engine declared. Sent whole and stored whole, so omitting a
   * key clears it and the game falls back to its own default.
   *
   * The admin API checks the keys and bounds against the engine's tunables
   * and nothing further: what a `minTarget` of `"12.50"` means is the game's
   * business, and it reaches the engine as `policy.minTarget` untouched.
   */
  policy: z.record(z.string(), z.unknown()).optional(),
});
export type SetOperatorGameRequest = z.infer<typeof SetOperatorGameRequest>;

export const AdminOperatorGameView = z.object({
  operatorId: z.string(),
  gameId: z.string(),
  gameCode: z.string(),
  enabled: z.boolean(),
  houseEdgeBps: z.number().nullable(),
  policy: z.record(z.string(), z.unknown()),
});
export type AdminOperatorGameView = z.infer<typeof AdminOperatorGameView>;

export const SetOperatorGameResponse = z.object({
  status: z.literal('OK'),
  operatorGame: AdminOperatorGameView,
});
export type SetOperatorGameResponse = z.infer<typeof SetOperatorGameResponse>;

export const AdminOperatorGamesListResponse = z.object({
  status: z.literal('OK'),
  games: z.array(AdminOperatorGameView),
});
export type AdminOperatorGamesListResponse = z.infer<typeof AdminOperatorGamesListResponse>;

// ---------------------------------------------------------------------------
// Operator game limits
// ---------------------------------------------------------------------------

function minorUnits(value: string): bigint | null {
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

/**
 * A ladder set by hand for one currency.
 *
 * The currency must be one whose minor unit the platform knows, because
 * every amount here is a count of that unit. Accepting an unknown code
 * would store a number with no scale attached to it, which is how the same
 * `10` came to mean ten cents and ten yen in the first place.
 */
export const SetOperatorGameLimitsRequest = z
  .object({
    currency: CurrencyCode,
    minBet: MinorUnits,
    maxBet: MinorUnits,
    betLevels: z.array(MinorUnits).min(1),
    defaultBetLevel: MinorUnits,
  })
  .refine((body) => isSupportedCurrency(body.currency), {
    path: ['currency'],
    error: 'the minor unit of this currency is unknown, so an amount in it has no scale',
  })
  // These run even when an amount failed its own check, so they read the
  // amounts defensively and leave that refusal to speak for itself.
  .refine((body) => (minorUnits(body.minBet) ?? 1n) >= 1n, {
    path: ['minBet'],
    error: 'the minimum must be at least one minor unit',
  })
  .refine((body) => (minorUnits(body.minBet) ?? 0n) <= (minorUnits(body.maxBet) ?? 0n), {
    path: ['maxBet'],
    error: 'the maximum cannot be below the minimum',
  })
  .refine((body) => body.betLevels.includes(body.defaultBetLevel), {
    path: ['defaultBetLevel'],
    error: 'the default must be one of the levels',
  });
export type SetOperatorGameLimitsRequest = z.infer<typeof SetOperatorGameLimitsRequest>;

/**
 * One currency's ladder as the admin API reports it.
 *
 * `derived` says where it came from: false is a row someone set, true is the
 * base ladder priced at `usdRate`. The portal needs the distinction to know
 * whether "remove override" has anything to remove.
 */
export const AdminBetLimitView = z.object({
  currency: z.string(),
  minBet: MinorUnits,
  maxBet: MinorUnits,
  betLevels: z.array(MinorUnits),
  defaultBetLevel: MinorUnits,
  derived: z.boolean(),
  /** The rate the ladder was priced at. Absent on a row that was set by hand. */
  usdRate: z.string().optional(),
});
export type AdminBetLimitView = z.infer<typeof AdminBetLimitView>;

export const AdminOperatorGameLimitsResponse = z.object({
  status: z.literal('OK'),
  limits: z.array(AdminBetLimitView),
});
export type AdminOperatorGameLimitsResponse = z.infer<typeof AdminOperatorGameLimitsResponse>;

// ---------------------------------------------------------------------------
// Exchange rates
// ---------------------------------------------------------------------------

export const AdminFxRateView = z.object({
  currency: z.string(),
  /** Units of the currency per one US dollar. */
  usdRate: z.string(),
  /** `casapi` is the feed; `manual` was pinned here and the feed will not touch it. */
  source: z.enum(['casapi', 'manual']),
  quotedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type AdminFxRateView = z.infer<typeof AdminFxRateView>;

export const AdminFxRatesResponse = z.object({
  status: z.literal('OK'),
  rates: z.array(AdminFxRateView),
});
export type AdminFxRatesResponse = z.infer<typeof AdminFxRatesResponse>;

export const SetFxRateRequest = z.object({
  /** A positive decimal: how many units of the currency one US dollar buys. */
  usdRate: z.string().regex(/^\+?\d+(?:\.\d+)?$/, 'must be a positive decimal'),
});
export type SetFxRateRequest = z.infer<typeof SetFxRateRequest>;

export const SetOperatorZeroEdgeLimitsRequest = z.object({
  currency: CurrencyCode,
  zeroEdgeMaxBet: MinorUnits,
  zeroEdgeDailyLimit: MinorUnits,
});
export type SetOperatorZeroEdgeLimitsRequest = z.infer<typeof SetOperatorZeroEdgeLimitsRequest>;

export const AdminZeroEdgeLimitView = z.object({
  currency: z.string(),
  zeroEdgeMaxBet: MinorUnits,
  zeroEdgeDailyLimit: MinorUnits,
});
export type AdminZeroEdgeLimitView = z.infer<typeof AdminZeroEdgeLimitView>;

export const AdminZeroEdgeLimitsResponse = z.object({
  status: z.literal('OK'),
  limits: z.array(AdminZeroEdgeLimitView),
});
export type AdminZeroEdgeLimitsResponse = z.infer<typeof AdminZeroEdgeLimitsResponse>;

// ---------------------------------------------------------------------------
// Operator risk config (exposure-based scaling edge)
// ---------------------------------------------------------------------------

export const SetOperatorRiskConfigRequest = z.object({
  currency: CurrencyCode,
  bankroll: MinorUnits,
  maxEdgeBps: z.number().int().min(1).max(9999).default(100),
});
export type SetOperatorRiskConfigRequest = z.infer<typeof SetOperatorRiskConfigRequest>;

export const AdminRiskConfigView = z.object({
  currency: z.string(),
  bankroll: MinorUnits,
  maxEdgeBps: z.number().int(),
});
export type AdminRiskConfigView = z.infer<typeof AdminRiskConfigView>;

export const AdminRiskConfigsResponse = z.object({
  status: z.literal('OK'),
  configs: z.array(AdminRiskConfigView),
});
export type AdminRiskConfigsResponse = z.infer<typeof AdminRiskConfigsResponse>;

// ---------------------------------------------------------------------------
// Thumbnail upload
// ---------------------------------------------------------------------------

export const ThumbnailUploadResponse = z.object({
  status: z.literal('OK'),
  thumbnailUrl: z.string(),
});
export type ThumbnailUploadResponse = z.infer<typeof ThumbnailUploadResponse>;

// ---------------------------------------------------------------------------
// Admin rounds (cross-tenant)
// ---------------------------------------------------------------------------

export const AdminRoundListQuery = z.object({
  operatorId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  playerId: z.string().optional(),
  gameCode: z.string().optional(),
  state: z
    .enum([
      'BET_PENDING',
      // A multi-step round between its first step and its last.
      'IN_PLAY',
      'RESOLVED',
      'SETTLE_PENDING',
      'CLOSED',
      'ROLLBACK_PENDING',
      'ROLLED_BACK',
      'FAILED',
    ])
    .optional(),
  mode: z.enum(['REAL', 'DEMO']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export type AdminRoundListQuery = z.infer<typeof AdminRoundListQuery>;

export const AdminRoundSummary = z.object({
  roundId: z.string(),
  operatorId: z.string(),
  operatorCode: z.string(),
  playerId: z.string(),
  gameCode: z.string(),
  currency: CurrencyCode,
  mode: z.enum(['REAL', 'DEMO']),
  betAmount: MinorUnits,
  winAmount: MinorUnits,
  state: z.string(),
  startedAt: z.string(),
  closedAt: z.string().nullable(),
});
export type AdminRoundSummary = z.infer<typeof AdminRoundSummary>;

export const AdminRoundListResponse = z.object({
  status: z.literal('OK'),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  rounds: z.array(AdminRoundSummary),
});
export type AdminRoundListResponse = z.infer<typeof AdminRoundListResponse>;

export const AdminTransactionRecord = z.object({
  txId: z.string(),
  type: z.enum(['BET', 'WIN', 'ROLLBACK']),
  amount: MinorUnits,
  currency: CurrencyCode,
  status: z.enum(['PENDING', 'CONFIRMED', 'FAILED', 'ROLLED_BACK']),
  operatorTxId: z.string().nullable(),
  balanceAfter: MinorUnits.nullable(),
  attempts: z.number().int(),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
});
export type AdminTransactionRecord = z.infer<typeof AdminTransactionRecord>;

export const AdminRoundDetail = AdminRoundSummary.extend({
  sessionId: z.string(),
  mathVersion: z.string(),
  rngSeed: z.string(),
  result: GameRoundResult.nullable(),
  /**
   * Whatever the engine returned as its working: the seeds, the cursor into
   * the stream, and anything else needed to arrive at the result again. The
   * platform does not read it, but an admin answering a disputed round has
   * nothing else to go on, so it is served as the engine wrote it.
   */
  engineProof: z.unknown().nullable(),
  transactions: z.array(AdminTransactionRecord),
});
export type AdminRoundDetail = z.infer<typeof AdminRoundDetail>;

export const AdminRoundDetailResponse = z.object({
  status: z.literal('OK'),
  round: AdminRoundDetail,
});
export type AdminRoundDetailResponse = z.infer<typeof AdminRoundDetailResponse>;

// ---------------------------------------------------------------------------
// Admin GGR reports (cross-tenant)
// ---------------------------------------------------------------------------

export const AdminGgrQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  operatorId: z.string().optional(),
  gameCode: z.string().optional(),
  currency: CurrencyCode.optional(),
  groupBy: z.enum(['DAY', 'GAME', 'CURRENCY', 'DAY_GAME', 'OPERATOR']).default('DAY'),
});
export type AdminGgrQuery = z.infer<typeof AdminGgrQuery>;

export const AdminGgrRow = z.object({
  date: z.string().optional(),
  gameCode: z.string().optional(),
  operatorId: z.string().optional(),
  currency: CurrencyCode,
  rounds: z.number().int(),
  players: z.number().int(),
  turnover: MinorUnits,
  payout: MinorUnits,
  ggr: SignedMinorUnits,
  rtp: z.number(),
});
export type AdminGgrRow = z.infer<typeof AdminGgrRow>;

export const AdminGgrResponse = z.object({
  status: z.literal('OK'),
  rows: z.array(AdminGgrRow),
});
export type AdminGgrResponse = z.infer<typeof AdminGgrResponse>;

// ---------------------------------------------------------------------------
// Admin cancel round / kick player
// ---------------------------------------------------------------------------

export const AdminCancelRoundRequest = z
  .object({
    reason: z.string().min(1).max(256).optional(),
  })
  .default({});
export type AdminCancelRoundRequest = z.infer<typeof AdminCancelRoundRequest>;

export const AdminCancelRoundResponse = z.object({
  status: z.literal('OK'),
  roundId: z.string(),
  state: z.string(),
  refundPending: z.boolean(),
});
export type AdminCancelRoundResponse = z.infer<typeof AdminCancelRoundResponse>;

export const AdminKickPlayerResponse = z.object({
  status: z.literal('OK'),
  playerId: z.string(),
  sessionsClosed: z.number().int().min(0),
});
export type AdminKickPlayerResponse = z.infer<typeof AdminKickPlayerResponse>;

// ---------------------------------------------------------------------------
// Engine registry (aggregator)
// ---------------------------------------------------------------------------

/**
 * Where an engine is in onboarding. Only `ENABLED` is routed: registering an
 * engine declares it, and it becomes playable by passing a gate.
 *
 * `QUARANTINED` is where an engine an admin switched off lands. It is kept
 * distinct from `REGISTERED` because it passed a gate once, and it needs a
 * fresh one to come back.
 */
export const AdminEngineStatus = z.enum([
  'REGISTERED',
  'GATING',
  'ENABLED',
  'GATE_FAILED',
  'QUARANTINED',
]);
export type AdminEngineStatus = z.infer<typeof AdminEngineStatus>;

export const AdminEngineCreate = z.object({
  gameCode: z.string().min(1).max(64),
  baseUrl: z.string().url(),
  adminBaseUrl: z.string().url().optional(),
});
export type AdminEngineCreate = z.infer<typeof AdminEngineCreate>;

export const AdminEngineView = z.object({
  id: z.string().uuid(),
  gameCode: z.string(),
  baseUrl: z.string(),
  adminBaseUrl: z.string().nullable(),
  status: AdminEngineStatus,
  /** Set when the engine last passed a gate. */
  gatedAt: z.string().nullable(),
  mathVersion: z.string().nullable(),
  contractVersion: z.string().nullable(),
  /** The image that passed. A change without a new gate is drift. */
  buildHash: z.string().nullable(),
  /**
   * What an operator may set for this game, as the engine declared it at
   * gate time. Empty until the engine has passed one, which is also when it
   * becomes routable, so there is no window where a setting can be offered
   * for a game nobody can play.
   */
  tunables: z.array(EngineTunable),
});
export type AdminEngineView = z.infer<typeof AdminEngineView>;

export const AdminEngineUpdate = z.object({
  baseUrl: z.string().url().optional(),
  adminBaseUrl: z.string().url().nullable().optional(),
  enabled: z.boolean().optional(),
});
export type AdminEngineUpdate = z.infer<typeof AdminEngineUpdate>;

/**
 * How the return figure in a gate result was arrived at.
 *
 * `GSP_MEASURED` means the platform played rounds and counted the money.
 * `ENGINE_SIMULATE` means the engine was asked what its return is and answered,
 * which is a claim rather than a measurement. Both can pass a gate; which one
 * happened is recorded, because it changes how much the number is worth.
 */
export const AdminEngineRtpSource = z.enum(['GSP_MEASURED', 'ENGINE_SIMULATE']);
export type AdminEngineRtpSource = z.infer<typeof AdminEngineRtpSource>;

/**
 * How thoroughly the engine's own test vectors were checked.
 *
 * `REPLAYED` means every vector was sent back to the running engine and the
 * response matched what was recorded. `STORED_ONLY` means the vectors were
 * kept as evidence but their inputs are not something a single call can
 * express — multi-step rounds, until the generic round contract lands — so
 * they were not replayed.
 */
export const AdminEngineVectorEvidence = z.enum(['REPLAYED', 'STORED_ONLY']);
export type AdminEngineVectorEvidence = z.infer<typeof AdminEngineVectorEvidence>;

/** A vector whose replay disagreed with what the engine recorded. */
export const AdminEngineVectorMismatch = z.object({
  id: z.string(),
  path: z.string(),
  expected: z.unknown(),
  actual: z.unknown(),
});
export type AdminEngineVectorMismatch = z.infer<typeof AdminEngineVectorMismatch>;

export const AdminEngineGateResult = z.object({
  passed: z.boolean(),
  vectorsPassed: z.boolean(),
  rtpOk: z.boolean(),
  versionOk: z.boolean(),
  /** Whether the engine speaks a contract version this platform can route. */
  contractOk: z.boolean(),
  vectors: z.number().optional(),
  rtp: z.number().optional(),
  expectedMathVersion: z.string().optional(),
  reportedMathVersion: z.string().optional(),
  message: z.string().optional(),
  /** The image the evidence belongs to; a later image has not been gated. */
  buildHash: z.string().nullish(),
  contractVersion: z.string().nullish(),
  schemasStored: z.number().optional(),
  vectorsReplayed: z.number().optional(),
  vectorsMatched: z.number().optional(),
  vectorEvidence: AdminEngineVectorEvidence.optional(),
  /** Capped: a failing gate should be readable, not a data dump. */
  vectorMismatches: z.array(AdminEngineVectorMismatch).optional(),
  rtpSource: AdminEngineRtpSource.optional(),
  roundsMeasured: z.number().optional(),
  /**
   * The spread of the measurement. A return figure without one says nothing:
   * a million rounds of a high-variance game can sit a long way from its true
   * return by chance alone, and the interval is what separates that from maths
   * that is actually wrong.
   */
  rtpStdError: z.number().optional(),
  rtpCi95: z.tuple([z.number(), z.number()]).optional(),
  expectedRtp: z.number().optional(),
});
export type AdminEngineGateResult = z.infer<typeof AdminEngineGateResult>;

/** A gate attempt, as the admin API reports it while and after it runs. */
export const AdminEngineGateJobView = z.object({
  id: z.string(),
  gameCode: z.string(),
  state: z.enum(['PENDING', 'RUNNING', 'PASSED', 'FAILED']),
  requestedBy: z.string().nullable(),
  roundsRequested: z.number(),
  attempts: z.number(),
  result: AdminEngineGateResult.nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type AdminEngineGateJobView = z.infer<typeof AdminEngineGateJobView>;
